import React, { useEffect, useState } from "react";
import styles from "./AdminAnalytics.module.scss";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import {
  AdminLicenseHolder,
  AdminPlatformMetrics,
  AdminSnapshotSummary,
  loadAdminAnalytics,
} from "./admin-real-data.service";
import ActivationFunnelChart from "./admin-components/ActivationFunnelChart";
import { APIService } from "../../shared/shared-with-mobile/api-client/api.service";
import { PlatformTimeseries } from "../../shared/shared-with-mobile/api-client/services/admin-analytics.service";
import ChartRangePicker, {
  useChartRange,
  filterByRange,
} from "./admin-components/ChartRangePicker";

// Aliases kept to minimize churn in the render below.
type LicenseHolder = AdminLicenseHolder;
type PlatformMetrics = AdminPlatformMetrics;
type SnapshotSummary = AdminSnapshotSummary;

const AdminAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([]);
  const [healthDist, setHealthDist] = useState<
    { range: string; count: number; category: string }[]
  >([]);
  const [tierDist, setTierDist] = useState<{ tier: string; count: number }[]>(
    []
  );
  const [timeseries, setTimeseries] = useState<PlatformTimeseries | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Fire all three loads in parallel. The bundle covers everything
        // except the daily-resolution event timeseries, which lives in
        // its own endpoint and may be slower (cached server-side).
        const [bundle, ts] = await Promise.all([
          loadAdminAnalytics(),
          APIService.ADMIN_ANALYTICS.GET_TIMESERIES().catch(() => null),
        ]);
        if (cancelled) return;
        setMetrics(bundle.metrics);
        setSnapshots(bundle.snapshots);
        setHealthDist(bundle.healthDist);
        setTierDist(bundle.tierDist.slice(0, 10));
        setTimeseries(ts);
      } catch (e) {
        console.error("Failed to load admin analytics", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // IMPORTANT: chart data computation + range hooks must run BEFORE
  // any early return so the React hook count is stable across renders.
  // First render: loading=true, hooks called with empty data → range
  // start defaults to today. Second render: loading=false with real
  // data → hooks see the real earliest dates and update accordingly.
  // Putting the hooks after the early return causes React to throw
  // "Rendered more hooks than during the previous render" and crash
  // the page to a blank screen.
  const PER_DAY_START_DATE = "2022-08-31";
  function withinDateFloor(rows: { date: string; count: number }[]) {
    return rows.filter((r) => r.date >= PER_DAY_START_DATE);
  }
  function runningSum(
    rows: { date: string; count: number }[]
  ): { x: string; y: number }[] {
    let total = 0;
    return rows.map((r) => {
      total += r.count;
      return { x: r.date, y: total };
    });
  }
  function asXY(
    rows: { date: string; count: number }[]
  ): { x: string; y: number }[] {
    return rows.map((r) => ({ x: r.date, y: r.count }));
  }

  // Pre-compute the full series once. Each chart filters by its own
  // selected range below (running-sum runs on the FULL data so the
  // cumulative line shows the running total at each visible day, not
  // a fresh sum starting from the visible window).
  const fullAccountsCumulative = timeseries
    ? runningSum(timeseries.accountsPerDay)
    : snapshots.map((s) => ({ x: s.date, y: s.totalAccounts }));
  const fullQuestionsCumulative = timeseries
    ? runningSum(withinDateFloor(timeseries.questionsPerDay))
    : snapshots.map((s) => ({ x: s.date, y: s.totalQuestions }));
  const fullQuestionsPerDay = timeseries
    ? asXY(withinDateFloor(timeseries.questionsPerDay))
    : [];
  const fullAccountsPerDay = timeseries ? asXY(timeseries.accountsPerDay) : [];

  const todayUTC = new Date().toISOString().slice(0, 10);

  // Combined cumulative chart spans the earlier of the two cumulative
  // series. Combined per-day chart same approach for the two daily series.
  function earliestOf(a?: string, b?: string): string {
    if (a && b) return a < b ? a : b;
    return a ?? b ?? todayUTC;
  }
  const earliestCumulative = earliestOf(
    fullAccountsCumulative[0]?.x,
    fullQuestionsCumulative[0]?.x
  );
  const earliestPerDay = earliestOf(
    fullQuestionsPerDay[0]?.x,
    fullAccountsPerDay[0]?.x
  );

  // One range hook per combined chart. Default = All Time → x-axis spans
  // earliest-data-point to today.
  const cumulativeRange = useChartRange(earliestCumulative);
  const perDayRange = useChartRange(earliestPerDay);

  if (loading || !metrics) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading analytics...</div>
      </div>
    );
  }

  // ----- Combined cumulative chart (Account Growth + Total Questions) -----
  // Nivo ResponsiveLine has a single y-scale, so the smaller series
  // (accounts ~9k) would visually flatline next to questions (~12M).
  // Trick: scale the questions series down to the accounts range before
  // plotting, then have axisRight tick labels multiply BACK to recover
  // the real value. The two lines stay visually proportional and each
  // axis shows the right magnitude for its series.
  const filteredAccountsCum = filterByRange(
    fullAccountsCumulative,
    cumulativeRange.range
  );
  const filteredQuestionsCum = filterByRange(
    fullQuestionsCumulative,
    cumulativeRange.range
  );
  const acctCumMax = Math.max(...filteredAccountsCum.map((d) => d.y), 1);
  const qCumMax = Math.max(...filteredQuestionsCum.map((d) => d.y), 1);
  const cumScale = qCumMax / acctCumMax;
  const cumulativeChartData = [
    { id: "Accounts", data: filteredAccountsCum },
    {
      id: "Total Questions",
      data: filteredQuestionsCum.map((d) => ({ x: d.x, y: d.y / cumScale })),
    },
  ];

  // ----- Combined per-day chart (Questions/Day + New Accounts/Day) -----
  const filteredQuestionsPerDay = filterByRange(
    fullQuestionsPerDay,
    perDayRange.range
  );
  const filteredAccountsPerDay = filterByRange(
    fullAccountsPerDay,
    perDayRange.range
  );
  const acctPdMax = Math.max(...filteredAccountsPerDay.map((d) => d.y), 1);
  const qPdMax = Math.max(...filteredQuestionsPerDay.map((d) => d.y), 1);
  const perDayScale = qPdMax / acctPdMax;
  const perDayChartData = [
    { id: "New Accounts", data: filteredAccountsPerDay },
    {
      id: "Questions",
      data: filteredQuestionsPerDay.map((d) => ({
        x: d.x,
        y: d.y / perDayScale,
      })),
    },
  ];
  const hasAnyPerDay = !!timeseries;

  // Health distribution for bar chart
  const healthBarData = healthDist.map((d) => ({
    range: d.range,
    count: d.count,
    color:
      d.category === "healthy"
        ? "#4CAF50"
        : d.category === "atRisk"
        ? "#FFC107"
        : "#F44336",
  }));

  const nivoTheme = {
    textColor: "#ababb7",
    fontSize: 11,
    grid: {
      line: {
        stroke: "#424258",
        strokeWidth: 1,
      },
    },
    axis: {
      ticks: {
        text: { fill: "#ababb7", fontSize: 10 },
      },
      legend: {
        text: { fill: "#ababb7", fontSize: 12 },
      },
    },
    crosshair: {
      line: {
        stroke: "#ffbc17",
        strokeWidth: 1,
      },
    },
    tooltip: {
      container: {
        background: "#282B3A",
        color: "#ffffff",
        fontSize: 12,
        borderRadius: 6,
        border: "1px solid #424258",
      },
    },
  };

  const sliceTooltipStyle: React.CSSProperties = {
    background: "#282B3A",
    color: "#ffffff",
    fontSize: 12,
    padding: 10,
    borderRadius: 6,
    border: "1px solid #424258",
    minWidth: 180,
  };

  const dualAxisLegends = [
    {
      anchor: "top-right" as const,
      direction: "row" as const,
      translateY: -22,
      translateX: 0,
      itemWidth: 130,
      itemHeight: 18,
      itemTextColor: "#ababb7",
      symbolSize: 10,
      symbolShape: "circle" as const,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Customer Analytics</h1>
          <p className={styles.subtitle}>
            Engagement, retention, and growth across all accounts
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>Total Accounts</div>
          <div className={styles.kpiValue}>
            {metrics.totalAccounts.toLocaleString()}
          </div>
          <div className={`${styles.kpiSub} ${styles.positive}`}>
            +{metrics.newThisMonth} this month
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>Paid Accounts</div>
          <div className={styles.kpiValue}>
            {metrics.paidAccounts.toLocaleString()}
          </div>
          <div className={styles.kpiSub}>
            {((metrics.paidAccounts / metrics.totalAccounts) * 100).toFixed(1)}%
            of total
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>Total Questions Answered</div>
          <div className={styles.kpiValue}>
            {(metrics.totalQuestions / 1000000).toFixed(1)}M
          </div>
          <div className={styles.kpiSub}>
            {metrics.totalUsers.toLocaleString()} total users
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>Avg Health Score</div>
          <div
            className={styles.kpiValue}
            style={{
              color:
                metrics.avgHealthScore >= 70
                  ? "#4CAF50"
                  : metrics.avgHealthScore >= 40
                  ? "#FFC107"
                  : "#F44336",
            }}
          >
            {metrics.avgHealthScore}
          </div>
          <div className={styles.kpiSub}>
            {metrics.healthyCount.toLocaleString()} healthy /{" "}
            {metrics.atRiskCount.toLocaleString()} at risk /{" "}
            {metrics.criticalCount.toLocaleString()} critical
          </div>
        </div>
      </div>

      {/* Combined cumulative chart: Account Growth + Total Questions.
          Full-width. Dual y-axis (accounts left, questions right). */}
      <div className={styles.card} style={{ marginBottom: 16 }}>
        <h3 className={styles.cardTitle}>
          Account Growth &amp; Total Questions Answered Over Time
        </h3>
        <ChartRangePicker
          earliest={earliestCumulative}
          selected={cumulativeRange.range}
          onChange={cumulativeRange.setRange}
        />
        <div style={{ height: 320 }}>
          <ResponsiveLine
            data={cumulativeChartData}
            theme={nivoTheme}
            margin={{ top: 30, right: 90, bottom: 60, left: 80 }}
            xScale={{
              type: "time",
              format: "%Y-%m-%d",
              precision: "day",
              useUTC: true,
              min: cumulativeRange.range.start,
              max: cumulativeRange.range.end,
            }}
            xFormat="time:%Y-%m-%d"
            yScale={{ type: "linear", min: 0, max: "auto" }}
            axisBottom={{
              format: "%b %Y",
              tickValues: 6,
              tickRotation: -45,
              legendOffset: 50,
            }}
            axisLeft={{
              format: (v) => (typeof v === "number" ? v.toLocaleString() : v),
              legend: "Accounts",
              legendPosition: "middle",
              legendOffset: -65,
            }}
            axisRight={{
              format: (v) =>
                typeof v === "number"
                  ? ((v * cumScale) / 1000000).toFixed(1) + "M"
                  : v,
              legend: "Total Questions",
              legendPosition: "middle",
              legendOffset: 75,
            }}
            colors={["#ffbc17", "#00b2ff"]}
            lineWidth={2.5}
            pointSize={0}
            useMesh
            enableGridX={false}
            enableSlices="x"
            sliceTooltip={({ slice }) => (
              <div style={sliceTooltipStyle}>
                <div style={{ marginBottom: 6, opacity: 0.8 }}>
                  {String(slice.points[0]?.data.xFormatted ?? "")}
                </div>
                {slice.points.map((point) => {
                  const isQuestions = point.serieId === "Total Questions";
                  const realY = Number(point.data.y);
                  const formatted = isQuestions
                    ? (realY * cumScale).toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })
                    : realY.toLocaleString();
                  return (
                    <div
                      key={point.id}
                      style={{ color: point.serieColor, marginTop: 2 }}
                    >
                      <strong>{point.serieId}:</strong> {formatted}
                    </div>
                  );
                })}
              </div>
            )}
            legends={dualAxisLegends}
          />
        </div>
      </div>

      {/* Combined per-day chart: Questions Per Day + New Accounts Per Day.
          Daily counts from question_statistic + license events. */}
      <div className={styles.card} style={{ marginBottom: 16 }}>
        <h3 className={styles.cardTitle}>
          Questions Answered &amp; New Accounts Per Day
        </h3>
        {!hasAnyPerDay ? (
          <p className={styles.subtitle} style={{ marginTop: 4 }}>
            Loading historical event data...
          </p>
        ) : (
          <>
            <ChartRangePicker
              earliest={earliestPerDay}
              selected={perDayRange.range}
              onChange={perDayRange.setRange}
            />
            <p className={styles.subtitle} style={{ marginTop: -4 }}>
              Real daily counts from the question_statistic event log and
              first-license-per-email signups.
            </p>
            <div style={{ height: 320 }}>
              <ResponsiveLine
                data={perDayChartData}
                theme={nivoTheme}
                margin={{ top: 30, right: 90, bottom: 60, left: 80 }}
                xScale={{
                  type: "time",
                  format: "%Y-%m-%d",
                  precision: "day",
                  useUTC: true,
                  min: perDayRange.range.start,
                  max: perDayRange.range.end,
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{ type: "linear", min: 0, max: "auto" }}
                axisBottom={{
                  format: "%b %Y",
                  tickValues: 6,
                  tickRotation: -45,
                  legendOffset: 50,
                }}
                axisLeft={{
                  format: (v) =>
                    typeof v === "number" ? v.toLocaleString() : v,
                  legend: "New Accounts / Day",
                  legendPosition: "middle",
                  legendOffset: -65,
                }}
                axisRight={{
                  format: (v) =>
                    typeof v === "number"
                      ? Math.round(v * perDayScale).toLocaleString()
                      : v,
                  legend: "Questions / Day",
                  legendPosition: "middle",
                  legendOffset: 75,
                }}
                colors={["#ffbc17", "#35d3ed"]}
                lineWidth={1.5}
                pointSize={0}
                useMesh
                enableGridX={false}
                enableSlices="x"
                sliceTooltip={({ slice }) => (
                  <div style={sliceTooltipStyle}>
                    <div style={{ marginBottom: 6, opacity: 0.8 }}>
                      {String(slice.points[0]?.data.xFormatted ?? "")}
                    </div>
                    {slice.points.map((point) => {
                      const isQuestions = point.serieId === "Questions";
                      const realY = Number(point.data.y);
                      const formatted = isQuestions
                        ? Math.round(realY * perDayScale).toLocaleString()
                        : realY.toLocaleString();
                      return (
                        <div
                          key={point.id}
                          style={{ color: point.serieColor, marginTop: 2 }}
                        >
                          <strong>{point.serieId}:</strong> {formatted}
                        </div>
                      );
                    })}
                  </div>
                )}
                legends={dualAxisLegends}
              />
            </div>
          </>
        )}
      </div>

      {/* Health Distribution & Tier Breakdown */}
      <div className={styles.cardGrid}>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Health Score Distribution</h3>
          <div className={styles.healthSummary}>
            <div
              className={styles.healthBucket}
              style={{ background: "rgba(76,175,80,0.1)" }}
            >
              <div
                className={styles.healthBucketValue}
                style={{ color: "#4CAF50" }}
              >
                {metrics.healthyCount.toLocaleString()}
              </div>
              <div className={styles.healthBucketLabel}>Healthy (70+)</div>
              <div style={{ color: "#4CAF50", fontSize: 11 }}>
                {((metrics.healthyCount / metrics.totalAccounts) * 100).toFixed(
                  1
                )}
                %
              </div>
            </div>
            <div
              className={styles.healthBucket}
              style={{ background: "rgba(255,193,7,0.1)" }}
            >
              <div
                className={styles.healthBucketValue}
                style={{ color: "#FFC107" }}
              >
                {metrics.atRiskCount.toLocaleString()}
              </div>
              <div className={styles.healthBucketLabel}>At Risk (40-69)</div>
              <div style={{ color: "#FFC107", fontSize: 11 }}>
                {((metrics.atRiskCount / metrics.totalAccounts) * 100).toFixed(
                  1
                )}
                %
              </div>
            </div>
            <div
              className={styles.healthBucket}
              style={{ background: "rgba(244,67,54,0.1)" }}
            >
              <div
                className={styles.healthBucketValue}
                style={{ color: "#F44336" }}
              >
                {metrics.criticalCount.toLocaleString()}
              </div>
              <div className={styles.healthBucketLabel}>Critical (&lt;40)</div>
              <div style={{ color: "#F44336", fontSize: 11 }}>
                {(
                  (metrics.criticalCount / metrics.totalAccounts) *
                  100
                ).toFixed(1)}
                %
              </div>
            </div>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveBar
              data={healthBarData}
              theme={nivoTheme}
              indexBy="range"
              keys={["count"]}
              margin={{ top: 10, right: 10, bottom: 40, left: 50 }}
              colors={({ data }) => (data as any).color}
              borderRadius={3}
              axisBottom={{
                tickRotation: -45,
              }}
              axisLeft={{
                format: (v) => (typeof v === "number" ? v.toLocaleString() : v),
              }}
              enableLabel={false}
              enableGridX={false}
            />
          </div>
        </div>

        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Tier Distribution</h3>
          <div style={{ height: 340 }}>
            <ResponsiveBar
              data={tierDist.map((t) => ({
                tier: t.tier.length > 20 ? t.tier.slice(0, 18) + "..." : t.tier,
                count: t.count,
              }))}
              theme={nivoTheme}
              indexBy="tier"
              keys={["count"]}
              layout="horizontal"
              margin={{ top: 10, right: 40, bottom: 10, left: 140 }}
              colors={["#ffbc17"]}
              borderRadius={3}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
              }}
              axisBottom={undefined as any}
              enableLabel={true}
              labelTextColor="#151724"
              enableGridX={false}
              enableGridY={false}
            />
          </div>
        </div>
      </div>

      {/* Activation funnel — for the cohort of license-holders who signed
          up in the selected window, what fraction reached each stage:
          team created → play added → first session → 50+ questions. */}
      <div className={styles.card} style={{ marginBottom: 16 }}>
        <h3 className={styles.cardTitle}>Activation Funnel</h3>
        <p
          className={styles.subtitle}
          style={{ marginTop: -8, marginBottom: 12 }}
        >
          Where new signups drop off. Computed live from the raw event tables —
          license + team + play + question_statistic. Cached 1h.
        </p>
        <ActivationFunnelChart />
      </div>

      {/* Customer Engagement table moved to its own /admin/customers tab. */}
    </div>
  );
};

export default AdminAnalytics;
